import {
  Injectable,
  computed,
  inject,
  signal
} from '@angular/core';
import { Observable, of, tap } from 'rxjs';

import { AuthState } from '../users';

import {
  SteamProfileStatus,
  StateConditions,
  ApiResponse,
  UserCooldown
} from './promocodes.entity';
import { PromocodesService } from './promocodes.service';

@Injectable({ providedIn: 'root' })
export class PromocodesState {
  private readonly authState = inject(AuthState);
  private readonly promocodesService = inject(PromocodesService);

  public readonly cooldown = signal<UserCooldown|null>(null);
  public readonly userStatus = signal<SteamProfileStatus|null>(null);
  public readonly conditions = signal<StateConditions|null>(null);

  public readonly userConditions = computed(() => {
    const userStatus = this.userStatus();
    const conditions = this.conditions();

    if(!userStatus || !conditions) {
      return {
        minDeposit: false,
        publicProfile: false,
        steamLevel: false,
        csgoHours: false
      };
    }

    return {
      minDeposit: userStatus.depositedUsd >= conditions.minDepositUsd,
      publicProfile: (userStatus.isProfilePublic && conditions.isPublicProfileRequired) || !conditions.isPublicProfileRequired,
      steamLevel: userStatus.steamLevel >= conditions.minSteamLevel,
      csgoHours: userStatus.csgoHours >= conditions.minCsgoHours
    };
  });

  public readonly hasCooldown = computed(() => !!this.cooldown());

  loadUserStatus(): Observable<ApiResponse.UserStatus|null> {
    if(!this.authState.isAuthenticated()) {
      return of(null);
    }

    return this.promocodesService.getUserStatus().pipe(
      tap((response: ApiResponse.UserStatus) => {
        if(response?.cooldownStartAt && response?.cooldownEndsAt) {
          this.cooldown.set({
            startAt: response?.cooldownStartAt,
            endAt: response?.cooldownEndsAt
          });
        }

        this.userStatus.set(response.userStatus);
        this.conditions.set(response.conditions);
      })
    );
  }

  usePromocode(code: string): Observable<ApiResponse.UsedPromocode> {
    return this.promocodesService.usePromocode(code).pipe(
      tap((response: ApiResponse.UsedPromocode) => {
        if(response?.result.cooldownStartAt && response?.result.cooldownEndsAt) {
          this.cooldown.set({
            startAt: response?.result.cooldownStartAt,
            endAt: response?.result.cooldownEndsAt
          });
        } else if (response.type !== 'secret_code') {
          this.cooldown.set(null);
        }
      })
    );
  }

  recheckConditions(): Observable<ApiResponse.RecheckedProfile> {
    return this.promocodesService.checkStatus().pipe(
      tap((response: ApiResponse.RecheckedProfile) => {
        const userStatus = this.userStatus();

        if(!userStatus) {
          return;
        }

        this.userStatus.set({
          ...userStatus,
          ...response
        });
      })
    );
  }

  reset(): void {
    this.cooldown.set(null);
    this.userStatus.set(null);
    this.conditions.set(null);
  }
}
